import { Controller } from '@hotwired/stimulus';
import AutoSave from 'stimulus/auto_save';

export default class extends Controller {
  static targets = ['values', 'chips', 'chipTemplate'];

  static values = {
    name: String,
    autoSaveUrl: String,
    max: { type: Number, default: 0 },
    debounce: { type: Number, default: 500 }
  };

  connect() {
    this.autoSave = new AutoSave({
      url: this.autoSaveUrlValue,
      name: this.nameValue,
      debounce: this.debounceValue,
      label: this.identifier,
      element: this.element
    });
  }

  disconnect() {
    this.autoSave?.cancel();
  }

  pick(event) {
    const { value, label } = event.detail;
    if (!value) return;

    const field = this.selectField();
    if (this.hasValue(value) || this.full()) {
      field?.reset();
      return;
    }

    this.appendChip(value, label);
    this.appendHiddenInput(value);
    field?.take(value);
    field?.reset();
    this.chipsTarget.hidden = false;
    this.announceChange();
    this.dispatch('added', { detail: { value, label } });
  }

  remove(event) {
    this.drop(String(event.params.value));
  }

  // Removal by value rather than by click, so a controller pairing two of these can take a
  // duplicate out of one side. `remove` is the same operation reached from the chip.
  drop(value, { notify = true } = {}) {
    if (!value) return;

    const item = this.chipFor(value);
    if (!item) return;

    const label = item.querySelector('.ui-chip__label').textContent.trim();

    item.remove();

    this.valuesTarget.querySelectorAll('input').forEach((input) => {
      if (input.value === value) input.remove();
    });

    this.selectField()?.release(value, label);

    if (!this.chipsTarget.querySelector('.ui-search-input__item')) {
      this.chipsTarget.hidden = true;
    }

    if (notify) this.announceChange();
    this.dispatch('removed', { detail: { value, label } });
  }

  // One save for the whole field, rather than one per value on the way down.
  clear() {
    const values = this.values();
    if (values.length === 0) return;

    values.forEach(value => this.drop(value, { notify: false }));
    this.announceChange();
  }

  take(value) {
    if (!this.hasValue(value)) this.selectField()?.take(value);
  }

  release(value, label) {
    if (!this.hasValue(value)) this.selectField()?.release(value, label);
  }

  values() {
    return Array.from(this.valuesTarget.querySelectorAll('input'), input => input.value)
      .filter(Boolean);
  }

  full() {
    return this.maxValue > 0 && this.values().length >= this.maxValue;
  }

  chipFor(value) {
    const selector = `[data-search-input-component-value-param="${CSS.escape(value)}"]`;

    return this.chipsTarget.querySelector(selector)?.closest('.ui-search-input__item');
  }

  appendChip(value, label) {
    const item = this.chipTemplateTarget.content.firstElementChild.cloneNode(true);
    item.dataset.value = value;
    item.querySelector('.ui-chip__label').textContent = label;

    const removeButton = item.querySelector('.ui-micro-button');
    removeButton.setAttribute('aria-label', `Remove ${label}`);
    removeButton.setAttribute(`data-${this.identifier}-value-param`, value);

    this.chipsTarget.appendChild(item);
  }

  // Fired from the blank input the list always carries, which outlives every value.
  announceChange() {
    this.valuesTarget.querySelector('input')
      ?.dispatchEvent(new Event('change', { bubbles: true }));

    if (this.autoSaveUrlValue) this.autoSave.saveNow(this.submittedValues());
  }

  submittedValues() {
    return Array.from(this.valuesTarget.querySelectorAll('input'), input => input.value);
  }

  appendHiddenInput(value) {
    const sentinel = this.valuesTarget.querySelector('input');
    const input = sentinel.cloneNode();
    input.value = value;
    this.valuesTarget.appendChild(input);
  }

  hasValue(value) {
    return Array.from(this.valuesTarget.querySelectorAll('input'))
      .some((input) => input.value === value);
  }

  selectField() {
    const element = this.element.querySelector('[data-controller~="select-field-component"]');
    if (!element) return null;
    return this.application.getControllerForElementAndIdentifier(element, 'select-field-component');
  }
}
